import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, RotateCcw, Trash2 } from 'lucide-react';
import { Button, Dialog, Input } from '@/design-system';
import type { TrashItem } from '@/domain/history/contracts';
import { navigate } from '@/app/router';
import * as api from './api/trash-api';

export function TrashPage() {
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TrashItem | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems((await api.listTrash()).items);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível carregar a lixeira.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function restore(item: TrashItem) {
    setBusyId(item.id);
    setError(null);
    try {
      await api.restoreTrashItem(item.id);
      setItems((current) => current.filter((candidate) => candidate.id !== item.id));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Não foi possível restaurar o funil.');
    } finally {
      setBusyId(null);
    }
  }

  async function permanentlyDelete() {
    if (!deleteTarget || confirmation !== deleteTarget.name) return;
    setBusyId(deleteTarget.id);
    setError(null);
    try {
      await api.permanentlyDeleteTrashItem(deleteTarget.id, confirmation);
      setItems((current) => current.filter((candidate) => candidate.id !== deleteTarget.id));
      setDeleteTarget(null);
      setConfirmation('');
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'A exclusão permanente falhou.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-8">
      <button type="button" onClick={() => navigate('/app')} className="mb-5 flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary">
        <ArrowLeft size={16} /> Voltar aos funis
      </button>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text-primary">Lixeira</h1>
        <p className="mt-1 text-sm text-text-secondary">Os funis excluídos ficam aqui até serem restaurados ou excluídos permanentemente.</p>
      </div>
      {error ? <p role="alert" className="mb-4 text-sm text-[#f08484]">{error}</p> : null}
      {loading ? (
        <p role="status" className="py-12 text-center text-sm text-text-muted">Carregando lixeira…</p>
      ) : items.length === 0 ? (
        <p className="rounded border border-border-subtle py-12 text-center text-sm text-text-muted">A lixeira está vazia.</p>
      ) : (
        <ul className="space-y-3" aria-label="Funis excluídos">
          {items.map((item) => (
            <li key={item.id} className="flex items-center justify-between gap-4 rounded border border-border-subtle bg-surface p-4">
              <div>
                <h2 className="font-medium text-text-primary">{item.name}</h2>
                <p className="text-xs text-text-muted">Excluído em {new Date(item.deletedAt).toLocaleString('pt-BR')} · antes: {item.previousStatus}{item.folderName ? ` · ${item.folderName}` : ''}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" disabled={busyId === item.id} onClick={() => void restore(item)}><RotateCcw size={15} /> Restaurar</Button>
                <Button variant="danger" disabled={busyId === item.id} onClick={() => { setDeleteTarget(item); setConfirmation(''); }}><Trash2 size={15} /> Excluir permanentemente</Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} title="Excluir funil permanentemente">
        <p className="mb-3 text-sm text-text-secondary">Isso remove o funil, as versões, dados de compartilhamento e ativos privados. Esta ação não pode ser desfeita.</p>
        <label className="text-sm text-text-secondary">
          Digite <strong className="text-text-primary">{deleteTarget?.name}</strong> para confirmar
          <Input className="mt-1" aria-label="Confirmação do nome do funil" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button variant="danger" disabled={!deleteTarget || confirmation !== deleteTarget.name || busyId !== null} onClick={() => void permanentlyDelete()}>Excluir permanentemente</Button>
        </div>
      </Dialog>
    </main>
  );
}
